"use client";

import { useEffect, useState } from "react";
import { WorkspaceImage } from "@/app/components/WorkspaceImage";
import {
    DEFAULT_WORKSPACE_AVATAR,
    DEFAULT_WORKSPACE_ICON_SURFACE,
    resolveWorkspaceIconComponent,
    type WorkspaceAvatarSelection,
} from "@/lib/workspaceAvatar";

type WorkspaceAvatarDisplayProps = {
    avatar: WorkspaceAvatarSelection;
    size?: number;
    /** Override lucide icon size inside the avatar tile */
    iconSize?: number;
    className?: string;
    fallbackClassName?: string;
};

export function WorkspaceAvatarDisplay({
    avatar,
    size = 48,
    iconSize: iconSizeProp,
    className = "",
    fallbackClassName = "bg-aera-lightgray border border-aera-lightgray",
}: WorkspaceAvatarDisplayProps) {
    const iconSize = iconSizeProp ?? Math.round(size * 0.42);
    const [imageFailed, setImageFailed] = useState(false);

    useEffect(() => {
        setImageFailed(false);
    }, [avatar.mode, avatar.mode === "image" ? avatar.preview : null]);

    if (avatar.mode === "image" && avatar.preview?.trim() && !imageFailed) {
        return (
            <div
                className={`overflow-hidden rounded-lg flex items-center justify-center relative ${className}`}
                style={{ width: size, height: size }}
            >
                <WorkspaceImage
                    src={avatar.preview}
                    alt=""
                    className="h-full w-full object-cover"
                    onError={() => setImageFailed(true)}
                />
            </div>
        );
    }

    if (avatar.mode === "icon" || (avatar.mode === "image" && imageFailed)) {
        const iconAvatar =
            avatar.mode === "icon"
                ? avatar
                : DEFAULT_WORKSPACE_AVATAR;
        const Icon = resolveWorkspaceIconComponent(iconAvatar.iconKey);
        const isDefault =
            iconAvatar.iconKey === DEFAULT_WORKSPACE_AVATAR.iconKey &&
            iconAvatar.color === DEFAULT_WORKSPACE_AVATAR.color;

        const iconColor = isDefault ? DEFAULT_WORKSPACE_AVATAR.color : iconAvatar.color;

        return (
            <div
                className={`rounded-lg flex items-center justify-center border ${className}`}
                style={{
                    width: size,
                    height: size,
                    backgroundColor: isDefault
                        ? DEFAULT_WORKSPACE_ICON_SURFACE
                        : `${iconColor}18`,
                    borderColor: `${iconColor}30`,
                }}
            >
                <Icon
                    size={iconSize}
                    style={{ color: iconColor }}
                    strokeWidth={2}
                />
            </div>
        );
    }

    const DefaultIcon = resolveWorkspaceIconComponent(DEFAULT_WORKSPACE_AVATAR.iconKey);
    return (
        <div
            className={`rounded-lg flex items-center justify-center ${fallbackClassName} ${className}`}
            style={{
                width: size,
                height: size,
                backgroundColor: DEFAULT_WORKSPACE_ICON_SURFACE,
            }}
        >
            <DefaultIcon size={iconSize} style={{ color: "#171717" }} strokeWidth={2} />
        </div>
    );
}
